import { createPinia } from 'pinia';
import { createApp } from 'vue';
import App from './App.vue';
import { router } from './router.js';
import { useSession } from './stores/session.js';
import { useSkin } from './stores/skin.js';
import { useViewport } from './stores/viewport.js';
import { applyBoard, boardFor } from './ui/board.js';
import { clientEvents } from './ui/client-events.js';
import { clearStaleLabKeys } from './ui/skin-scope.js';
import { configureTracker, flushTrack, installTrackFlush, messageHash, trackEvent } from './ui/track.js';
import '@cg/design-tokens/css';
import './styles/base.css';
import './styles/themes.css';
// 学生端 H5 的手机版式:全部写在 `.h5 …` 下面,<html> 没有这个 class(电脑、平板)时一条都不生效
import './styles/h5.css';

const pinia = createPinia();
const app = createApp(App).use(pinia).use(router);

// 埋点:默认记到当前站点与登录账号上、带端标识(手机上是 h5);页面切走时把队列发掉
const session = useSession(pinia);
const viewport = useViewport(pinia);
configureTracker(() => ({
  tenantCode: session.tenantCode || undefined,
  accessToken: session.accessToken,
  client: viewport.client,
}));
installTrackFlush();
const pageKey = () => String(router.currentRoute.value.name ?? 'unknown').slice(0, 64);
// 前端报错只报指纹(不带原文与堆栈),同一条一页只报一次
const reported = new Set<string>();
function reportError(msg: string) {
  const h = messageHash(msg);
  if (reported.has(h)) return;
  reported.add(h);
  trackEvent(clientEvents.pageError(h, pageKey()));
}
app.config.errorHandler = (err, _vm, info) => {
  reportError(`${info}:${err instanceof Error ? err.message : String(err)}`);
  console.error(err);
};
window.addEventListener('error', (e) => reportError(e.message || 'error'));
window.addEventListener('unhandledrejection', (e) =>
  reportError(e.reason instanceof Error ? e.reason.message : String(e.reason)),
);
// 首屏最大内容绘制(LCP):页面第一次隐藏时报一次;服务端按 1/5 采样
try {
  let lcp = 0;
  new PerformanceObserver((list) => {
    const last = list.getEntries().at(-1);
    if (last) lcp = Math.round(last.startTime);
  }).observe({ type: 'largest-contentful-paint', buffered: true });
  let sent = false;
  document.addEventListener('visibilitychange', () => {
    if (sent || document.visibilityState !== 'hidden' || !lcp) return;
    sent = true;
    trackEvent(clientEvents.pageLoad(lcp, pageKey()));
    flushTrack();
  });
} catch {
  /* 不支持 LCP 的浏览器不报 */
}

// 样式试验台(2026-10-12 下线)在本机留过的记录:没有代码再读它们,启动时顺手删掉(一次性的小清理,过几个版本可以去掉)
clearStaleLabKeys(
  (() => {
    try {
      return window.localStorage;
    } catch {
      return null; // 隐私模式下一碰就抛
    }
  })(),
);

// 皮肤先于首屏:令牌与图标就位后再挂载,避免闪一下别的样子。
// 套哪套只看页面(ui/skin-scope.ts):学生端 / 家长端页面是「深色外壳」shell,其余页面 default。
// 这里先按地址栏这一页定一次,之后每次进页面前(守卫)按最终落到的页面再定
const skin = useSkin(pinia);
// 页面的最小宽度(画板)也是按页面定的(ui/board.ts):前台 1212,后台 1440,自适应的页面(官网首页)不设。和皮肤一样,先按地址栏定一次,之后每次进页面前再定
const first = router.resolve(window.location.pathname + window.location.search);
applyBoard(document.documentElement, boardFor(first));
router.beforeResolve((to) => {
  applyBoard(document.documentElement, boardFor(to));
  return skin.enter(to);
});
await skin.enter(first);
app.mount('#app');
